import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CatCrest } from "./CatCrest";
import { site, contact, hours, socials, legal } from "../data/siteConfig";
import { socialPendingToast } from "../lib/notify";
import { FacebookIcon, InstagramIcon, TikTokIcon } from "./SocialIcons";
import { X } from "lucide-react";

const SOCIAL_ICONS = { facebook: FacebookIcon, instagram: InstagramIcon, tiktok: TikTokIcon };

const LegalDialog = ({ title, body, onClose, testid }) => (
    <motion.div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-obsidian/90 p-5 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testid}
    >
        <motion.div
            className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/10 bg-coal p-8"
            initial={{ scale: 0.95, y: 16 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 16 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-start justify-between gap-4">
                <h3 className="font-serif text-2xl font-semibold text-cream">
                    {title}
                </h3>
                <button
                    onClick={onClose}
                    aria-label={`Close ${title}`}
                    data-testid={`${testid}-close`}
                    className="rounded-full border border-white/20 p-2 text-cream transition-colors duration-200 hover:border-stone-400 hover:text-stone-300"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-stone-300">{body}</p>
        </motion.div>
    </motion.div>
);

export const Footer = () => {
    const [dialog, setDialog] = useState(null);

    return (
        <footer
            className="border-t border-white/10 bg-obsidian pb-28 pt-16 text-cream md:pb-16"
            data-testid="site-footer"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-4">
                            <CatCrest className="h-14 w-14 text-cream" showText />
                            <div>
                                <p className="font-serif text-xl font-semibold">
                                    {site.name}
                                </p>
                                <p className="mt-1 font-serif text-sm italic text-cream/60">
                                    {site.tagline}
                                </p>
                            </div>
                        </div>
                        <p className="mt-6 max-w-sm text-sm leading-relaxed text-stone-400">
                            {contact.address.line1}, {contact.address.line2},{" "}
                            {contact.address.country}
                        </p>
                    </div>

                    <div className="md:col-span-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/40">
                            Explore
                        </p>
                        <nav
                            className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm"
                            aria-label="Footer"
                        >
                            {[
                                ["The Cattery", "#cattery", "footer-cattery-link"],
                                ["Care", "#care", "footer-care-link"],
                                ["Prices & Hours", "#pricing", "footer-pricing-link"],
                                ["Gallery", "#gallery", "footer-gallery-link"],
                                ["Reviews", "#reviews", "footer-reviews-link"],
                                ["Contact / Book", "#book", "footer-book-link"],
                                ["Our Story", "#story", "footer-story-link"],
                            ].map(([label, href, testid]) => (
                                <a
                                    key={href}
                                    href={href}
                                    data-testid={testid}
                                    className="text-stone-400 transition-colors duration-200 hover:text-cream"
                                >
                                    {label}
                                </a>
                            ))}
                        </nav>
                    </div>

                    <div className="md:col-span-3">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/40">
                            Visit
                        </p>
                        <p className="mt-4 text-sm leading-relaxed text-stone-400">
                            {hours.days}
                            <br />
                            {hours.morning}
                            <br />
                            {hours.afternoon}
                        </p>
                        <div className="mt-5 flex gap-2">
                            {socials.map((s) => {
                                const Icon = SOCIAL_ICONS[s.id];
                                return s.url ? (
                                    <a
                                        key={s.id}
                                        href={s.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`footer-social-${s.id}`}
                                        aria-label={`${s.label} — ${s.account}`}
                                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-stone-300 transition-[border-color,color] duration-200 hover:border-stone-400 hover:text-cream"
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        {s.label}
                                    </a>
                                ) : (
                                    <button
                                        key={s.id}
                                        onClick={() => socialPendingToast(s.label, s.account)}
                                        data-testid={`footer-social-${s.id}`}
                                        aria-label={`${s.label} — ${s.account}`}
                                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-stone-300 transition-[border-color,color] duration-200 hover:border-stone-400 hover:text-cream"
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        {s.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
                    <p className="text-xs text-stone-500">
                        © {new Date().getFullYear()} {site.name} · Mākara,
                        Wellington, New Zealand
                    </p>
                    <div className="flex gap-5 text-xs">
                        <a
                            href="#/terms-and-conditions"
                            data-testid="footer-terms-button"
                            className="text-stone-500 transition-colors duration-200 hover:text-cream"
                        >
                            Terms &amp; Conditions
                        </a>
                        <button
                            onClick={() => setDialog("privacy")}
                            data-testid="footer-privacy-button"
                            className="text-stone-500 transition-colors duration-200 hover:text-cream"
                        >
                            Privacy
                        </button>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {dialog === "privacy" && (
                    <LegalDialog
                        title="Privacy"
                        body={legal.privacy}
                        onClose={() => setDialog(null)}
                        testid="privacy-dialog"
                    />
                )}
            </AnimatePresence>
        </footer>
    );
};
